import Box from './assets/Individual/Box.png';
import Cry from './assets/Individual/Cry.png';
import Dance from './assets/Individual/Dance.png';
import Eating from './assets/Individual/Eating.png';
import Excited from './assets/Individual/Excited.png';
import Idle from './assets/Individual/Idle.png';
import LayDown from './assets/Individual/LayDown.png';
import Sad from './assets/Individual/Sad.png';
import Sleep from './assets/Individual/Sleep.png';
import Sleepy from './assets/Individual/Sleepy.png';
import Surprised from './assets/Individual/Surprised.png';
import Waiting from './assets/Individual/Waiting.png';

import { useEffect, useState, useRef } from 'react';
import { isNil } from 'lodash';

export enum CatSpriteType {
  Box = 'Box',
  Cry = 'Cry',
  Dance = 'Dance',
  Eating = 'Eating',
  Excited = 'Excited',
  Idle = 'Idle',
  LayDown = 'LayDown',
  Sad = 'Sad',
  Sleep = 'Sleep',
  Sleepy = 'Sleepy',
  Surprised = 'Surprised',
  Waiting = 'Waiting',
}

const SPRITE_TO_IMAGE: Record<CatSpriteType, string | StaticImageData> = {
  [CatSpriteType.Box]: Box,
  [CatSpriteType.Cry]: Cry,
  [CatSpriteType.Dance]: Dance,
  [CatSpriteType.Eating]: Eating,
  [CatSpriteType.Excited]: Excited,
  [CatSpriteType.Idle]: Idle,
  [CatSpriteType.LayDown]: LayDown,
  [CatSpriteType.Sad]: Sad,
  [CatSpriteType.Sleep]: Sleep,
  [CatSpriteType.Sleepy]: Sleepy,
  [CatSpriteType.Surprised]: Surprised,
  [CatSpriteType.Waiting]: Waiting,
};

const SPRITE_TO_FRAME_COUNT: Record<CatSpriteType, number> = {
  [CatSpriteType.Box]: 12,
  [CatSpriteType.Cry]: 4,
  [CatSpriteType.Dance]: 4,
  [CatSpriteType.Eating]: 15,
  [CatSpriteType.Excited]: 12,
  [CatSpriteType.Idle]: 10,
  [CatSpriteType.LayDown]: 12,
  [CatSpriteType.Sad]: 9,
  [CatSpriteType.Sleep]: 4,
  [CatSpriteType.Sleepy]: 8,
  [CatSpriteType.Surprised]: 12,
  [CatSpriteType.Waiting]: 6,
};

interface SpriteAnimationProps {
  imageType: CatSpriteType;
  frameWidth?: number;
  frameHeight?: number;
}

const preloadedImages = new Map<CatSpriteType, HTMLImageElement>();
const preloadAllImages = () => {
  Object.entries(SPRITE_TO_IMAGE).forEach(([spriteType, imageSrc]) => {
    const img = new Image();
    img.src = typeof imageSrc === 'string' ? imageSrc : imageSrc.src;
    preloadedImages.set(spriteType as CatSpriteType, img);
  });
};
preloadAllImages();

export function CatSprite({ imageType, frameWidth = 32, frameHeight = 32 }: SpriteAnimationProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const frameCount = SPRITE_TO_FRAME_COUNT[imageType];
  const frameDurations = Array(frameCount).fill(150);

  useEffect(() => {
    const preloadedImg = preloadedImages.get(imageType);
    if (preloadedImg && preloadedImg.complete) {
      setImage(preloadedImg);
    } else if (preloadedImg) {
      preloadedImg.onload = () => setImage(preloadedImg);
    } else {
      // Fallback if preloading failed
      const img = new Image();
      const catImage = SPRITE_TO_IMAGE[imageType];
      img.src = typeof catImage === 'string' ? catImage : catImage.src;
      img.onload = () => setImage(img);
    }
  }, [imageType]);

  useEffect(() => {
    if (!image) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;

    let frameIndex = 0;
    let lastTime = performance.now();
    let accumulated = 0;
    let animationId: number | undefined = undefined;

    const loop = (time: number) => {
      const delta = time - lastTime;
      lastTime = time;
      accumulated += delta;

      if (accumulated >= frameDurations[frameIndex]) {
        frameIndex = (frameIndex + 1) % frameCount;
        accumulated = 0;
      }

      ctx.clearRect(0, 0, frameWidth, frameHeight);
      ctx.drawImage(
        image,
        frameIndex * frameWidth,
        0,
        frameWidth,
        frameHeight,
        0,
        0,
        frameWidth,
        frameHeight,
      );

      animationId = requestAnimationFrame(loop);
    };

    animationId = requestAnimationFrame(loop);

    return () => {
      if (!isNil(animationId)) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [image, frameCount, frameWidth, frameHeight, frameDurations]);

  return (
    <canvas
      ref={canvasRef}
      width={frameWidth}
      height={frameHeight}
      style={{ width: '32px', height: '32px' }}
    />
  );
}
